import { ValidationRules } from 'aurelia-validation';
import { Division } from './division';
import { Equipment } from './equipment';
import { CarrierLane } from './carrier-lane';
import { Contact } from './contact';

export class Carrier {
    dedicatedScacCodes: string;
    diversityCertified: boolean;
    diversityCategories: Array<any>;
    hazmatCertified: boolean;
    haulScrap: boolean;
    sustainability: boolean;
    hasSustainabilityProgram: boolean; // this column is used to drive UI, but is not stored in DB
    sustainabilityProgram: string;
    mcNumber: string;
    additionalMcNumbers: string;
    alternateName: string;
    dedicatedFleet: boolean;
    isMasterApprovedEmailSent: boolean;
    contacts: Contact[] = [];
    ospCarrierProductGroups: string[] = [];
    primaryAgentId: string;
    primaryAgentIdOverridden: boolean;
    carrierPointId: string;

    constructor() {
        //these arrays are only used when sending data to the server.
        //individual web api calls are made when stored in separate properties when displaying in the UI.
        this.equipment = [];
        this.diversityCategories = [];
        this.existingCarrierDivisions = [];
        this.preferredInboundLanes = [];
        this.preferredOutboundLanes = [];
        this.status = 0; //new
    }

    public get isTrucksCarrier(): boolean {
        return this.entityType == "TRUCKS" || this.entityType == 'TRANSLOADER/TRUCKS';
    }

    public get isBargeCarrier(): boolean {
        return this.entityType == "BARGE";
    }

    id: number;
    status: number;
    statusDate: Date;
    legalName: string;
    dbaName: string;
    contractType: string;
    scacCode: string;
    dotNumber: string;

    legalAddressStreet: string;
    legalAddressCity: string;
    legalAddressState: string;
    legalAddressZip: string;
    legalAddressCountry: string;
    safetyRating: string;
    taxId: string;
    mainPhone: string;
    mainPhoneExt: string;
    //ownerOfficerName: string;
    //ownerOfficerTitle: string;
    //ownerOfficerEmail: string;
    //ownerOfficerPhone: string;
    //ownerOfficerPhoneExt: string;
    //administratorName: string;
    //administratorEmail: string;
    //administratorPhone: string;
    //administratorPhoneExt: string;
    userHasReviewed: boolean;
    typeOfAuthority: string;
    isSubmitted: boolean;
    numberOfTrucks: number;
    hasWaterway: boolean;
    hasTrucks: boolean;
    existingCarrier: boolean;
    existingCarrierDivisions: Array<Division>;
    preferredInboundLanes: Array<CarrierLane>;
    preferredOutboundLanes: Array<CarrierLane>;
    equipment: Array<Equipment>;
    parent: number;
    parentName: string;
    newComment: string;
    communicationType: string;
    uniqueId: string;
    confinedToState: boolean;
    versionCol: string;
    deleted: boolean;
    priority: boolean;
    documentHold: boolean;
    entityType: string;
    millReferrer: string;
    children: any[];
    ngtvid: string;
    ngtvidLocked: boolean;
    overlengthHauler: boolean;
    heavyHauler: boolean;
    heavyHaulerWeightId: number;

    getCarrierName() {
        return this.dbaName || this.legalName;
    }
}

ValidationRules.customRule(
    'existingCarrierDivisions',
    (value, obj) => !obj.existingCarrier || (obj.existingCarrier && obj.existingCarrierDivisions.length > 0),
    `\${$displayName} must be specified.`
);

ValidationRules.customRule(
    'heavyHaulerWeightId',
    (value, obj) => !obj.heavyHauler || !!obj.heavyHaulerWeightId,
    `\${$displayName} must be specified.`
);
ValidationRules.customRule(
    'hasSustainabilityProgram',
    (value, obj) => !obj.sustainability || !!obj.hasSustainabilityProgram,
    `\${$displayName} must be specified.`
);

ValidationRules.customRule(
    'numeric',
    (value, obj) => !value || !isNaN(parseInt(value.toString())),
    `\${$displayName} must be numeric.`
);

ValidationRules.customRule(
    'sustainabilityProgram',
    (value, obj) => !obj.hasSustainabilityProgram || !!obj.sustainabilityProgram,
    `\${$displayName} must be specified.`
);

ValidationRules.customRule(
    'diversityCategories',
    (value, obj) => !obj.diversityCertified || !!obj.diversityCategories.length,
    `\${$displayName} must be specified.`
);


ValidationRules.customRule(
    'mandatoryContacts',
    (value, obj: Carrier) => {
        if (obj.contacts) {
            let hasAllMandatoryTypes = true;
            const mandatoryContactTypeIds = [
                2, // owner
                1, // administrator
                7, // account rep
                12, // rate contact
                3, // dispatch
                42, // new business contact
                46, // 24 hrcontact
                11 // insurance contact
            ];
            mandatoryContactTypeIds.forEach(ctypeId => {
                if (!(obj.hasTrucks || obj.isTrucksCarrier)) {
                    // for non-trucks, the rate and dispatch contacts are not required.
                    if (ctypeId == 3 || ctypeId == 12) {
                        return;
                    }
                }
                if (!obj.contacts.find(c => !!c.contactTypeIds.find(ct => ct == ctypeId))) {
                    hasAllMandatoryTypes = false;
                }
            });
            return hasAllMandatoryTypes;
        }
        return false;
    },
    `\Contact information must be provided for each of the roles listed in the "Contact Information" section.`
);

ValidationRules
    .ensure((m: Carrier) => m.legalName).required()
    .ensure((m: Carrier) => m.taxId).satisfies((val, obj) => obj.isTrucksCarrier || !!val)
    .ensure((m: Carrier) => m.dotNumber).satisfies((val, obj) => !obj.isTrucksCarrier || !!val)
    .ensure((m: Carrier) => m.typeOfAuthority).required()
    .ensure((m: Carrier) => m.hasWaterway).satisfies((val, obj) => !obj.isBargeCarrier || !!val)

    .ensure((m: Carrier) => m.legalAddressStreet).required()
    .ensure((m: Carrier) => m.legalAddressCity).required()
    .ensure((m: Carrier) => m.legalAddressState).required()
    .ensure((m: Carrier) => m.legalAddressZip).required()
    .ensure((m: Carrier) => m.legalAddressCountry).required()

    .ensure((m: Carrier) => m.mainPhone).required().satisfiesRule("phone")

    //.ensure((m: Carrier) => m.ownerOfficerName).required()
    //.ensure((m: Carrier) => m.ownerOfficerTitle).required()
    //.ensure((m: Carrier) => m.ownerOfficerEmail).required().email()
    //.ensure((m: Carrier) => m.ownerOfficerPhone).required().satisfiesRule("phone")

    //.ensure((m: Carrier) => m.administratorName).required()
    //.ensure((m: Carrier) => m.administratorEmail).required().email()
    //.ensure((m: Carrier) => m.administratorPhone).required().satisfiesRule("phone")

    .ensure((m: Carrier) => m.userHasReviewed).satisfiesRule("isChecked")

    .ensure((m: Carrier) => m.haulScrap).required()
    
    .ensure((m: Carrier) => m.hasTrucks).required()
    .ensure((m: Carrier) => m.existingCarrier).required()
    .ensure((m: Carrier) => m.hasWaterway).satisfies((val, obj) => obj.isTrucksCarrier || val != undefined)
    .ensure((m: Carrier) => m.numberOfTrucks).satisfies((val, obj) => !obj.isTrucksCarrier || !!val)
    .ensure((m: Carrier) => m.existingCarrierDivisions).satisfiesRule("existingCarrierDivisions")
    .ensure((m: Carrier) => m.equipment).satisfiesRule("arrayIsNotEmpty")
    .ensure((m: Carrier) => m.preferredInboundLanes).satisfies((val, obj) => !obj.isTrucksCarrier || !!val.length)
    .ensure((m: Carrier) => m.preferredOutboundLanes).satisfies((val, obj) => !obj.isTrucksCarrier || !!val.length)

    .ensure((m: Carrier) => m.overlengthHauler).required()
    .ensure((m: Carrier) => m.communicationType).required()
    .ensure((m: Carrier) => m.heavyHauler).required()
    .ensure((m: Carrier) => m.diversityCertified).required()
    .ensure((m: Carrier) => m.diversityCategories).satisfiesRule("diversityCategories")
    .ensure((m: Carrier) => m.hazmatCertified).required()
    .ensure((m: Carrier) => m.sustainability).required()
    .ensure((m: Carrier) => m.hasSustainabilityProgram).satisfiesRule("hasSustainabilityProgram")
    .ensure((m: Carrier) => m.sustainabilityProgram).satisfiesRule("sustainabilityProgram")
    .ensure((m: Carrier) => m.heavyHaulerWeightId).satisfiesRule("heavyHaulerWeightId")
    .ensure((m: Carrier) => m.contacts).satisfiesRule("mandatoryContacts")

    .on(Carrier);